import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProcessResponse } from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { ProcessPublicController } from "api/main/ProcessPublicController";
import { ProcessExecutionPublicController } from "api/main/ProcessExecutionPublicController";
import { adminTheme } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

export default function UserProcessList() {
  const api = useRef(createApi("processpublic") as ProcessPublicController);
  const executionApi = useRef(createApi("processexecution") as ProcessExecutionPublicController);
  const navigate = useNavigate();
  const [processes, setProcesses] = useState<ProcessResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingProcess, setStartingProcess] = useState<ProcessResponse | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    getData();
  }, []);

  async function getData() {
    setLoading(true);
    try {
      const data = await api.current.findAll();
      // Only show active processes
      const activeProcesses = (data || []).filter((p: ProcessResponse) => p.active);
      setProcesses(activeProcesses);
    } catch (error) {
      console.error("Error fetching processes:", error);
    }
    setLoading(false);
  }

  const handleStartProcess = (process: ProcessResponse) => {
    setStartingProcess(process);
    setConfirmDialogOpen(true);
  };

  const confirmStartProcess = async () => {
    if (!startingProcess?.id) return;

    setLoading(true);
    setConfirmDialogOpen(false);

    try {
      const execution = await executionApi.current.create({
        processDefinitionId: startingProcess.id,
      });
      if (execution?.id) {
        navigate(`/process/execution/${execution.id}/0`);
      }
    } catch (error) {
      console.error("Error starting process:", error);
    }
    setLoading(false);
    setStartingProcess(null);
  };

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: adminTheme.colors.textPrimary,
            }}
          >
            Galimi procesai
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: adminTheme.colors.textSecondary }}
          >
            Pasirinkite ir pradėkite vykdyti procesą
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Table */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: adminTheme.colors.backgroundAlt,
                    "& th": {
                      fontWeight: 600,
                      color: adminTheme.colors.textPrimary,
                      borderBottom: `2px solid ${adminTheme.colors.border}`,
                      py: 2,
                    },
                  }}
                >
                  <TableCell>Proceso pavadinimas</TableCell>
                  <TableCell>Žingsnių skaičius</TableCell>
                  <TableCell align="right">Veiksmas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processes.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: adminTheme.colors.textMuted,
                        }}
                      >
                        <AccountTreeIcon
                          sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}
                        />
                        <Typography>Šiuo metu nėra galimų procesų</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  processes.map((process) => (
                    <TableRow
                      key={process.id}
                      sx={{
                        transition: adminTheme.transitions.fast,
                        "&:hover": {
                          bgcolor: "rgba(99, 102, 241, 0.04)",
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: adminTheme.borderRadius.sm,
                              bgcolor: "rgba(99, 102, 241, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: adminTheme.colors.primary,
                            }}
                          >
                            <AccountTreeIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: adminTheme.colors.textPrimary,
                            }}
                          >
                            {process.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${process.steps?.length || 0} žingsniai`}
                          size="small"
                          sx={{
                            bgcolor: adminTheme.colors.backgroundAlt,
                            color: adminTheme.colors.textSecondary,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => handleStartProcess(process)}
                          sx={{
                            background: adminTheme.gradients.primary,
                            "&:hover": { background: adminTheme.gradients.primaryHover },
                          }}
                        >
                          Pradėti
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: adminTheme.borderRadius.lg },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Pradėti procesą?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: adminTheme.colors.textSecondary }}>
            Ar tikrai norite pradėti procesą "{startingProcess?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            sx={{ color: adminTheme.colors.textSecondary }}
          >
            Atšaukti
          </Button>
          <Button
            variant="contained"
            onClick={confirmStartProcess}
            sx={{
              background: adminTheme.gradients.primary,
              "&:hover": { background: adminTheme.gradients.primaryHover },
            }}
          >
            Pradėti
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
